import { useMemo, useRef, useState } from 'react';

import { EMedicalCategoryType } from '@/entities/medical-category';
import useSearchDoctorsInfinity from '@/entities/telemedicine/hooks/useSearchDoctorsInfinity';
import useCustomRouter from '@/hooks/useCustomRouter';
import {
  DiagnosisCategoryType,
  EDiagnosisStep,
  GeoPoint,
  TreatmentRequest,
  TreatmentRequestDoctor,
  WebDiagnosisTreatmentRequestPageQueries,
} from '@/models/diagnosis';
import { EDoctorFilterType } from '@/models/diagnosis/doctor-filter';
import { EDoctorSortType } from '@/models/doctor/telemedicine';
import { parseMedicalCategoryCategoryTypeOf } from '@/parsers/common';
import { ROUTES } from '@/types/common';
import { addQueryString } from '@/utils/url';
import { useToast } from 'doctornow-design-system';
import { DOCTOR_SORT_KEY } from '../ui';
import { useDoctorFilterToggle } from './useDoctorFilterToggle';

interface UseTelemedicineDoctorSelectionParams {
  queries: WebDiagnosisTreatmentRequestPageQueries;
  treatmentRequest: TreatmentRequest;
  updateFormFromTreatmentRequest: (request: TreatmentRequest) => void;
  step: EDiagnosisStep;
}

export const useTelemedicineDoctorSelection = ({
  queries,
  treatmentRequest,
  updateFormFromTreatmentRequest,
  step,
}: UseTelemedicineDoctorSelectionParams) => {
  const { toast } = useToast();
  const { router } = useCustomRouter();
  const { type, categoryId } = queries;

  const shouldEnableQuery = step === EDiagnosisStep.Doctor;

  const [isSearchDoctorSortFilterBottomSheetOpen, setIsSearchDoctorSortFilterBottomSheetOpen] = useState(false);
  const [doctorPublicId, setDoctorPublicId] = useState<string | null>(null);
  const doctorDetailOpenCountRef = useRef(0);
  const originalTypeRef = useRef<DiagnosisCategoryType | undefined>(undefined);

  const { selectedFilters, specialistOnly, highReviewRating, highReviewCount, handleToggleFilter } =
    useDoctorFilterToggle();

  const { searchDoctors, isLoadingSearchDoctors, fetchNextPageSearchDoctors, hasNextPageSearchDoctors } =
    useSearchDoctorsInfinity({
      categoryType: type,
      categoryId,
      targetDate: treatmentRequest.form.bookingTargetDate,
      startTimeSlotValue: treatmentRequest.form.startTimeSlotValue,
      endTimeSlotValue: treatmentRequest.form.endTimeSlotValue,
      sortType: treatmentRequest.form.sortKey,
      specialistOnly,
      highReviewRating,
      highReviewCount,
      geoPoint: treatmentRequest.getGeoPoint(),
      enabled: shouldEnableQuery,
    });

  const openSearchDoctorSortFilterBottomSheet = () => {
    setIsSearchDoctorSortFilterBottomSheetOpen(true);
  };

  const closeSearchDoctorSortFilterBottomSheet = () => {
    setIsSearchDoctorSortFilterBottomSheetOpen(false);
  };

  const requestGeolocation = (): Promise<GeoPoint> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: false, timeout: 7000 }
      );
    });
  };

  const handleSearchDoctorSortFilterSelect = async (sortKey: DOCTOR_SORT_KEY) => {
    if (sortKey.value === EDoctorSortType.DISTANCE) {
      try {
        const geoPoint = await requestGeolocation();
        treatmentRequest.updateGeoPoint(geoPoint);
      } catch {
        toast({
          text: '위치 권한 허용 후 다시 시도해주세요',
          direction: 'bottom',
        });
        return;
      }
    }

    treatmentRequest.updateSortKey(sortKey.value);
    updateFormFromTreatmentRequest(treatmentRequest);

    router.replace(
      `${ROUTES.TELEMEDICINE.REQUEST.path}` +
        addQueryString({
          ...router.query,
          sortKey: sortKey.value,
        })
    );

    closeSearchDoctorSortFilterBottomSheet();

    toast({
      text: sortKey.toastMessage,
      direction: 'bottom',
    });
  };

  const handleSearchDoctorSpecialistOnlySelect = (isSpecialistOnly: boolean) => {
    const hasSpecialistFilter = selectedFilters.includes(EDoctorFilterType.SPECIALIST);

    if (isSpecialistOnly !== hasSpecialistFilter) {
      handleToggleFilter(EDoctorFilterType.SPECIALIST);
    }
  };

  const incrementDoctorDetailOpenCount = (): void => {
    doctorDetailOpenCountRef.current += 1;
  };

  const getDoctorDetailOpenCount = (): number => {
    return doctorDetailOpenCountRef.current;
  };

  const onOpenDoctorDetailBottomSheet = (doctorPublicId: string): void => {
    setDoctorPublicId(doctorPublicId);
    incrementDoctorDetailOpenCount();

    const convertedType = parseMedicalCategoryCategoryTypeOf(type ?? EMedicalCategoryType.DEPARTMENT);

    router.push(
      `${ROUTES.TELEMEDICINE.REQUEST.path}` +
        addQueryString({
          ...router.query,
          doctorId: doctorPublicId,
          type: convertedType,
        })
    );
  };

  const onCloseDoctorDetailBottomSheet = () => {
    router.back();
  };

  const getDoctorIdByQueryString = () => {
    return router.query.doctorId as string;
  };

  const onSelectDoctor = (doctor?: TreatmentRequestDoctor, doctorPublicId?: string) => {
    if (!doctor) {
      return;
    }
    treatmentRequest.initializeTreatmentRequestFromDoctor(doctor);
    if (doctorPublicId) {
      treatmentRequest.updateDoctorPid(doctorPublicId);
    }

    updateFormFromTreatmentRequest(treatmentRequest);
    router.push(
      `${ROUTES.TELEMEDICINE.REQUEST.path}` +
        addQueryString({
          type: originalTypeRef.current,
          categoryId: categoryId,
          step: EDiagnosisStep.Payment,
        })
    );
  };

  const isDoctorStepButtonDisabled = useMemo(() => {
    const category = treatmentRequest.getCategory();
    if (!category) {
      return true;
    }
    const baseValidation = !treatmentRequest.validateDiagnosisStepByWebDiagnosis();
    return baseValidation;
  }, [treatmentRequest]);

  const handleFilterSelectionNext = () => {
    if (selectedFilters.length === 0) {
      toast({
        text: '최소 1개 이상의 필터를 선택해주세요',
        direction: 'bottom',
      });
      return;
    }

    router.push(
      `${ROUTES.TELEMEDICINE.REQUEST.path}` +
        addQueryString({
          ...router.query,
          step: EDiagnosisStep.Doctor,
        })
    );
  };

  return {
    doctorPublicId,
    searchDoctors,
    isLoadingSearchDoctors,
    hasNextPageSearchDoctors,
    isSearchDoctorSortFilterBottomSheetOpen,
    setDoctorPublicId,
    fetchNextPageSearchDoctors,
    openSearchDoctorSortFilterBottomSheet,
    closeSearchDoctorSortFilterBottomSheet,
    handleSearchDoctorSortFilterSelect,
    handleSearchDoctorSpecialistOnlySelect,
    incrementDoctorDetailOpenCount,
    getDoctorDetailOpenCount,
    onOpenDoctorDetailBottomSheet,
    onCloseDoctorDetailBottomSheet,
    getDoctorIdByQueryString,
    onSelectDoctor,
    isDoctorStepButtonDisabled,
    selectedFilters,
    handleToggleFilter,
    handleFilterSelectionNext,
  };
};
